import { ComponentPropsWithRef, ElementType, ReactNode, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import type { IconComponent } from '../../themes/base/icons/types';
import { Variant, Size } from '../../types';
import { WithTooltip } from '../tooltip';
import {
    StyledButton,
    ButtonOption as StyledButtonOption,
    ButtonText,
    ButtonOptionTextContainer,
    ChildrenContainer,
    IconWrapper,
    MarginDirection,
    Spinner,
    ButtonOptionSelected,
} from './button.styles';
import { Icon } from '../icon';
import { buttonColor, CommonButtonProps } from './button.utils';
import { CheckCircleIcon } from '../../themes/base/icons/named';

export type ButtonProps<Component extends ElementType = 'button'> = {
    text?: string;
    icon?: IconComponent;
    fillParent?: boolean;
    iconAfter?: IconComponent;
    iconUseMargin?: boolean;
    padding?: null | string;
    showInPrint?: boolean;
    tooltip?: string;
    loading?: boolean;
    as?: Component;
} & Omit<ComponentPropsWithRef<Component>, 'size'> &
    CommonButtonProps;

export function Button<Component extends ElementType = 'button'>({
    children,
    ref,
    type = 'button',
    text,
    disabled = false,
    variant = Variant.PRIMARY,
    icon,
    name = 'undefined_button',
    fillParent = true,
    size = Size.NORMAL,
    iconAfter,
    iconUseMargin = false,
    showInPrint = false,
    tooltip,
    loading = false,
    ...rest
}: ButtonProps<Component>) {
    const theme = useTheme();
    const [showLoading, setShowLoading] = useState<boolean>(false);

    const iconSize = theme.componentSizes.button[size].iconSize;
    // Icon paints with the colour it is handed, so an explicit value wins over the
    // button's own `color`. Feed it the same props the styled button resolves its
    // text colour from, or the icon keeps the enabled colour while the label greys out.
    const iconColor = buttonColor({ variant, disabled, loading: showLoading, theme });

    useEffect(() => {
        if (loading) {
            const timerId = setTimeout(() => setShowLoading(true), 250);
            return () => clearTimeout(timerId);
        } else {
            setShowLoading(false);
        }
    }, [loading]);

    return (
        <WithTooltip text={tooltip}>
            <StyledButton
                ref={ref}
                type={type}
                disabled={disabled}
                data-test-id={name}
                showInPrint={showInPrint}
                variant={variant}
                fillParent={fillParent}
                size={size}
                loading={showLoading}
                {...rest}
            >
                {showLoading && <Spinner />}
                {icon && (
                    <IconWrapper
                        $marginDirection={MarginDirection.RIGHT}
                        $useMargin={iconUseMargin}
                    >
                        <Icon icon={icon} size={iconSize} color={iconColor} />
                    </IconWrapper>
                )}
                {children && <ChildrenContainer>{children}</ChildrenContainer>}
                {text && <ButtonText>{text}</ButtonText>}
                {iconAfter && (
                    <IconWrapper $useMargin={iconUseMargin} $marginDirection={MarginDirection.LEFT}>
                        <Icon icon={iconAfter} size={iconSize} color={iconColor} />
                    </IconWrapper>
                )}
            </StyledButton>
        </WithTooltip>
    );
}

export interface ButtonOptionsProps extends Omit<ButtonProps, 'title'> {
    title: ReactNode;
    description?: ReactNode;
    selected?: boolean;
}

export function ButtonOptions({
    children,
    onClick,
    type = 'button',
    disabled = false,
    variant = Variant.PRIMARY,
    name = 'undefined_button',
    fillParent = true,
    size = Size.NORMAL,
    iconUseMargin = false,
    title,
    description,
    padding,
    showInPrint = false,
    selected = false,
}: ButtonOptionsProps) {
    const theme = useTheme();
    return (
        <StyledButtonOption
            onClick={onClick}
            type={type}
            disabled={disabled}
            data-test-id={name}
            padding={padding}
            selected={selected}
            showInPrint={showInPrint}
            variant={variant}
            fillParent={fillParent}
            size={size}
        >
            {selected && (
                <ButtonOptionSelected>
                    <Icon
                        icon={CheckCircleIcon}
                        size={16}
                        color={disabled ? theme.buttonTextColorDisabled : theme.colorPrimary}
                    />
                </ButtonOptionSelected>
            )}
            {children && (
                <IconWrapper $useMargin={iconUseMargin} $marginDirection={MarginDirection.RIGHT}>
                    {children}
                </IconWrapper>
            )}
            <ButtonOptionTextContainer>
                {title}
                {description}
            </ButtonOptionTextContainer>
        </StyledButtonOption>
    );
}
